import dayjs from "dayjs";
import { DateTime } from "luxon";
import React, { useCallback, useContext, useMemo } from "react";
import { DATE_FORMAT } from "shared/components/common/datepicker/src/constants";
import { DatePickerDefaultShortcuts } from "shared/components/common/datepicker/src/constants/shortcuts";
import DatepickerContext from "shared/components/common/datepicker/src/contexts/DatepickerContext";
import {
  Period,
  ShortcutsItem,
} from "shared/components/common/datepicker/src/types";

interface ItemTemplateProps {
  children: JSX.Element;
  key: string;
  item: ShortcutsItem | ShortcutsItem[];
  id: string;
  tt: string;
}

// eslint-disable-next-line react/display-name
const ItemTemplate = React.memo((props: ItemTemplateProps) => {
  const {
    period,
    changePeriod,
    updateFirstDate,
    dayHover,
    changeDayHover,
    hideDatepicker,
    changeDatepickerValue,
  } = useContext(DatepickerContext);

  // Functions
  const chosePeriod = useCallback(
    (item: Period) => {
      if (dayHover) {
        changeDayHover(null);
      }
      if (period.start || period.end) {
        changePeriod({
          start: null,
          end: null,
        });
      }
      changePeriod(item);
      changeDatepickerValue({
        startDate: item.start,
        endDate: item.end,
      });
      updateFirstDate(dayjs(item.start));
      hideDatepicker();
    },
    [
      changeDatepickerValue,
      changeDayHover,
      changePeriod,
      dayHover,
      hideDatepicker,
      period.end,
      period.start,
      updateFirstDate,
    ],
  );

  const children = props?.children;

  return (
    <li
      className="whitespace-nowrap w-1/2 min-md:w-1/3 min-lg:w-auto transition-all duration-300 text-society hover:bg-gray-100 p-0.5 rounded cursor-pointer phone:whitespace-pre-line phone:min-h-sm phone:min-w-sm"
      id={`${props.id}-${props.tt}`}
      onClick={() => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        chosePeriod(props?.item.period);
      }}
    >
      {children}
    </li>
  );
});

interface ShortcustsProp {
  id: string;
  startingTime?: GetShortcutsProps;
}
type GetShortcutsProps = {
  startDay: number;
  startMonth: number;
};
const getShortcuts = (startingTime?: GetShortcutsProps) => {
  const curClock = DateTime.now();

  const DEFAULT_SHORTCUTS: {
    [key in DatePickerDefaultShortcuts]: ShortcutsItem | ShortcutsItem[];
  } = {
    today: {
      text: "Today",
      period: {
        start: curClock.toISODate(),
        end: curClock.toISODate(),
      },
    },
    yesterday: {
      text: "Yesterday",
      period: {
        start: curClock.minus({ day: 1 }).toISODate(),
        end: curClock.minus({ day: 1 }).toISODate(),
      },
    },
    past: [
      {
        daysNumber: 7,
        text: "Last 7 days",
        period: {
          start: curClock.minus({ day: 7 }).toISODate(),
          end: curClock.toISODate(),
        },
      },
      {
        daysNumber: 30,
        text: "Last 30 days",
        period: {
          start: curClock.minus({ day: 30 }).toISODate(),
          end: curClock.toISODate(),
        },
      },
    ],
    yearToDate: {
      text: "Year-to-Date",
      period: {
        start: DateTime.local(curClock.year, 1, 1).toISODate() ?? "",
        end: curClock.toISODate(),
      },
    },
    monthToDate: {
      text: "Month-to-Date",
      period: {
        start:
          DateTime.local(curClock.year, curClock.month, 1).toISODate() ?? "",
        end: curClock.toISODate(),
      },
    },
    previousWeek: {
      text: "Previous 7 Days",
      period: {
        start: curClock.minus({ day: 7 }).toISODate(),
        end: curClock.toISODate(),
      },
    },
    nextWeek: {
      text: "Next 7 Days",
      period: {
        start: curClock.plus({ day: 1 }).toISODate(),
        end: curClock.plus({ day: 8 }).toISODate(),
      },
    },
    currentYear: {
      text: "Current Year",
      period: {
        start: DateTime.local(curClock.year, 1, 1).toISODate() ?? "",
        end: DateTime.local(curClock.year, 12, 31).toISODate() ?? "",
      },
    },
    last3Years: {
      text: "Last 3 Years",
      period: {
        start: DateTime.local(curClock.year - 3, 1, 1).toISODate() ?? "",
        end: DateTime.local(curClock.year - 1, 12, 31).toISODate() ?? "",
      },
    },
    last5Years: {
      text: "Last 5 Years",
      period: {
        start: DateTime.local(curClock.year - 4, 1, 1).toISODate() ?? "",
        end: DateTime.local(curClock.year, 12, 31).toISODate() ?? "",
      },
    },
    last12Months: {
      text: "Last 12 Months",
      period: {
        start:
          DateTime.local(curClock.year, curClock.month, 1)
            .minus({ months: 12 })
            .toISODate() ?? "",
        end: DateTime.local(curClock.year, curClock.month, 1).toISODate() ?? "",
      },
    },
    last6Months: {
      text: "Last 6 Months",
      period: {
        start:
          DateTime.local(curClock.year, curClock.month, 1)
            .minus({ months: 6 })
            .toISODate() ?? "",
        end: DateTime.local(curClock.year, curClock.month, 1).toISODate() ?? "",
      },
    },
    futureYears: {
      text: "Future Years",
      period: {
        start: DateTime.local(curClock.year + 1, 1, 1).toISODate() ?? "",
        end: new Date(2999, 12, 30),
      },
    },
  };

  if (startingTime) {
    const curBillYear =
      startingTime.startMonth > curClock.month ||
      (startingTime.startMonth === 1 &&
        curClock.month === 1 &&
        startingTime.startDay > curClock.day)
        ? curClock.year - 1
        : curClock.year;
    const curBillMonth =
      startingTime.startDay <= curClock.day
        ? startingTime.startMonth
        : startingTime.startMonth - 1;
    const curBillClock = DateTime.local(curBillYear, curBillMonth, 1);

    const BILLING_SHORTCUTS = {
      previousBillingPeriod: {
        text: "Previous Billing Period",
        period: {
          start: curBillClock.minus({ year: 1 }).toISODate(),
          end: curBillClock.minus({ day: 1 }).toISODate(),
        },
      },
      currentBillingPeriod: {
        text: "Current Billing Period",
        period: {
          start: curBillClock.toISODate(),
          end: curBillClock.plus({ year: 1 }).minus(1).toISODate(),
        },
      },
      nextBillingPeriod: {
        text: "Next Billing Period",
        period: {
          start: curBillClock.plus({ year: 1 }).toISODate(),
          end: curBillClock.plus({ year: 2 }).minus(1).toISODate(),
        },
      },
    };
    Object.assign(DEFAULT_SHORTCUTS, BILLING_SHORTCUTS);
  }
  return DEFAULT_SHORTCUTS;
};
const Shortcuts = (prop: ShortcustsProp) => {
  // Contexts
  const { configs } = useContext(DatepickerContext);

  const callPastFunction = useCallback((data: unknown, numberValue: number) => {
    return typeof data === "function" ? data(numberValue) : null;
  }, []);

  const shortcutOptions = useMemo<
    [string, ShortcutsItem | ShortcutsItem[]][]
  >(() => {
    const DEFAULT_SHORTCUTS = getShortcuts(prop.startingTime);
    if (!configs?.shortcuts) {
      return Object.entries(DEFAULT_SHORTCUTS);
    }

    return Object.entries(configs.shortcuts).flatMap(([key, customConfig]) => {
      if (Object.prototype.hasOwnProperty.call(DEFAULT_SHORTCUTS, key)) {
        return [
          [key, DEFAULT_SHORTCUTS[key as keyof typeof DEFAULT_SHORTCUTS]],
        ];
      }

      const { text, period } = customConfig as {
        text: string;
        period: { start: string; end: string };
      };
      if (!text || !period) {
        return [];
      }

      const start = dayjs(period.start);
      const end = dayjs(period.end);

      if (
        start.isValid() &&
        end.isValid() &&
        (start.isBefore(end) || start.isSame(end))
      ) {
        return [
          [
            text,
            {
              text,
              period: {
                start: start.format(DATE_FORMAT),
                end: end.format(DATE_FORMAT),
              },
            },
          ],
        ];
      }

      return [];
    });
  }, [configs]);

  const printItemText = useCallback((item: ShortcutsItem) => {
    return item?.text ?? null;
  }, []);

  return shortcutOptions?.length ? (
    <div className="min-md:border-b mb-3 min-lg:mb-0 min-lg:border-r min-lg:border-b-0 border-neutral-mid-200 pr-1">
      <ul
        data-testid={`${prop.id}`}
        className="w-full list-none flex flex-wrap min-lg:flex-col pb-1 min-lg:pb-0"
      >
        {shortcutOptions.map(([key, item], index: number) =>
          Array.isArray(item) ? (
            item.map((item, index) => (
              <ItemTemplate
                id={prop.id}
                key={index + ""}
                item={item}
                tt={"fff"}
              >
                <>
                  {key === "past" &&
                  configs?.shortcuts &&
                  key in configs.shortcuts &&
                  item.daysNumber
                    ? callPastFunction(
                        configs.shortcuts[key as "past"],
                        item.daysNumber,
                      )
                    : item.text}
                </>
              </ItemTemplate>
            ))
          ) : (
            <ItemTemplate
              id={prop.id}
              key={index + ""}
              item={item}
              tt={index + ""}
            >
              <>
                {configs?.shortcuts && key in configs.shortcuts
                  ? typeof configs.shortcuts[
                      key as keyof typeof configs.shortcuts
                    ] === "object"
                    ? printItemText(item)
                    : configs.shortcuts[key as keyof typeof configs.shortcuts]
                  : printItemText(item)}
              </>
            </ItemTemplate>
          ),
        )}
      </ul>
    </div>
  ) : null;
};

export default Shortcuts;
